import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Calendar as CalendarIcon, ExternalLink, Filter, ShieldCheck } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { COUNTRIES } from "@/lib/constants";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const KIND_VALUES = ["concurso", "donacion", "credito", "estimulo_fiscal"];

export default function CulturalFunds() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "en" ? "en-US" : i18n.language === "pt" ? "pt-BR" : "es-ES";
  const [funds, setFunds] = useState([]);
  const [country, setCountry] = useState("all");
  const [kind, setKind] = useState("all");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const kinds = useMemo(
    () => KIND_VALUES.map((v) => ({ value: v, label: t(`funds.kind_${v}`) })),
    [t],
  );

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (country !== "all") params.set("country", country);
    if (kind !== "all") params.set("kind", kind);
    const { data } = await api.get(`/api/funds?${params.toString()}`);
    setFunds(data);
  }, [country, kind]);
  useEffect(() => { load(); }, [load]);

  const upcoming = funds
    .filter((f) => f.deadline)
    .sort((a, b) => new Date(a.deadline) - new Date(b.deadline))
    .slice(0, 4);

  const openEditor = (f = null) => {
    setEditing(f || { name: "", country: "Chile", kind: "concurso", deadline: "", institution: "", description: "", official_url: "", tax_benefit: "", requires_certification: false });
    setEditorOpen(true);
  };

  const saveFund = async () => {
    try {
      if (editing.id) await api.put(`/api/funds/${editing.id}`, editing);
      else await api.post("/api/funds", editing);
      toast.success(t("funds.toast_saved"));
      setEditorOpen(false);
      load();
    } catch (e) { toast.error(t("funds.toast_save_failed")); }
  };

  const removeFund = async (id) => {
    if (!window.confirm(t("funds.confirm_delete"))) return;
    await api.delete(`/api/funds/${id}`);
    load();
  };

  return (
    <DashboardLayout
      title={t("funds.title")}
      subtitle={t("funds.subtitle")}
      actions={
        user?.role === "admin" ? (
          <Button onClick={() => openEditor(null)} data-testid="button-new-fund" className="bg-petroleum text-white hover:bg-petroleum/90 rounded-full">
            {t("funds.new_fund")}
          </Button>
        ) : null
      }
    >
      <div className="grid lg:grid-cols-[1fr_320px] gap-6">
        <div>
          <div className="flex flex-wrap gap-3 mb-6 items-center">
            <Filter size={14} className="text-slate-500" />
            <div className="w-56">
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger data-testid="select-fund-country"><SelectValue placeholder={t("funds.filter_country_placeholder")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("funds.all_countries")}</SelectItem>
                  {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="w-56">
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger data-testid="select-fund-kind"><SelectValue placeholder={t("funds.filter_kind_placeholder")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("funds.all_kinds")}</SelectItem>
                  {kinds.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <span className="text-xs text-slate-500 ml-auto">{t("funds.count_suffix", { count: funds.length })}</span>
          </div>

          {funds.length === 0 ? (
            <div className="kpi-card p-8 text-center text-slate-500">{t("funds.empty")}</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {funds.map((f) => (
                <div key={f.id} className="kpi-card p-5" data-testid={`fund-${f.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className="bg-gold/15 text-gold hover:bg-gold/15 border-0 uppercase tracking-widest text-[10px]">{f.country}</Badge>
                        <Badge variant="secondary" className="text-[10px]">{f.kind}</Badge>
                        {f.requires_certification && <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 text-[10px]"><ShieldCheck size={10} className="inline mr-1" />{t("funds.badge_certified")}</Badge>}
                      </div>
                      <h3 className="font-display text-xl text-petroleum mt-2">{f.name}</h3>
                      {f.institution && <p className="text-xs text-slate-500 mt-1">{f.institution}</p>}
                    </div>
                  </div>
                  {f.description && <p className="text-sm text-slate-600 mt-3 line-clamp-3">{f.description}</p>}
                  {f.tax_benefit && (
                    <p className="text-sm text-emerald-700 mt-2 bg-emerald-50 rounded-lg px-3 py-1.5 inline-block">
                      {t("funds.benefit_prefix", { benefit: f.tax_benefit })}
                    </p>
                  )}
                  <div className="mt-4 flex items-center justify-between">
                    {f.deadline && (
                      <span className="text-sm text-slate-600 inline-flex items-center gap-1.5">
                        <CalendarIcon size={12} className="text-gold" /> {new Date(f.deadline).toLocaleDateString(dateLocale)}
                      </span>
                    )}
                    <div className="flex gap-3">
                      {f.official_url && (
                        <a href={f.official_url} target="_blank" rel="noreferrer" className="text-xs text-petroleum hover:text-gold inline-flex items-center gap-1">
                          {t("funds.official_link")} <ExternalLink size={10} />
                        </a>
                      )}
                      {user?.role === "admin" && (
                        <>
                          <button onClick={() => openEditor(f)} className="text-xs text-petroleum hover:text-gold">{t("funds.edit")}</button>
                          <button onClick={() => removeFund(f.id)} className="text-xs text-red-600">{t("funds.delete")}</button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <aside>
          <h3 className="section-title mb-4 flex items-center gap-2"><CalendarIcon size={16} className="text-gold" /> {t("funds.upcoming_title")}</h3>
          <div className="kpi-card p-5 space-y-4">
            {upcoming.length === 0 ? (
              <p className="text-sm text-slate-500">{t("funds.no_upcoming")}</p>
            ) : upcoming.map((f) => {
              const d = new Date(f.deadline);
              const days = Math.ceil((d - new Date()) / (1000 * 60 * 60 * 24));
              return (
                <div key={f.id} className="border-b border-slate-100 pb-3 last:border-0 last:pb-0">
                  <p className="text-xs uppercase tracking-widest text-slate-500">{f.country}</p>
                  <p className="text-sm font-medium text-petroleum mt-1 line-clamp-2">{f.name}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-slate-500">{d.toLocaleDateString(dateLocale)}</span>
                    <span className={`text-xs font-semibold ${days < 15 ? "text-red-600" : days < 45 ? "text-amber-600" : "text-emerald-600"}`}>
                      {days > 0 ? t("funds.days_left", { days }) : t("funds.closed")}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="kpi-card p-5 mt-4 bg-petroleum text-white border-0">
            <p className="text-xs uppercase tracking-widest text-gold">{t("funds.reminder_title")}</p>
            <p className="text-sm mt-2 text-white/80">
              {t("funds.reminder_text")}
            </p>
          </div>
        </aside>
      </div>

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing?.id ? t("funds.edit_fund_title") : t("funds.new_fund_title")}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="grid gap-3 py-2">
              <div>
                <Label>{t("funds.label_name")}</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("funds.label_country")}</Label>
                  <Select value={editing.country} onValueChange={(v) => setEditing({ ...editing, country: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t("funds.label_kind")}</Label>
                  <Select value={editing.kind} onValueChange={(v) => setEditing({ ...editing, kind: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{kinds.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>{t("funds.label_institution")}</Label>
                <Input value={editing.institution || ""} onChange={(e) => setEditing({ ...editing, institution: e.target.value })} />
              </div>
              <div>
                <Label>{t("funds.label_description")}</Label>
                <Textarea rows={3} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t("funds.label_deadline")}</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left">
                        <CalendarIcon size={14} className="mr-2" />
                        {editing.deadline ? new Date(editing.deadline).toLocaleDateString(dateLocale) : t("funds.deadline_pick")}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={editing.deadline ? new Date(editing.deadline) : undefined}
                        onSelect={(d) => setEditing({ ...editing, deadline: d ? d.toISOString().slice(0, 10) : "" })}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label>{t("funds.label_tax_benefit")}</Label>
                  <Input value={editing.tax_benefit || ""} onChange={(e) => setEditing({ ...editing, tax_benefit: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>{t("funds.label_official_url")}</Label>
                <Input value={editing.official_url || ""} onChange={(e) => setEditing({ ...editing, official_url: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditorOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={saveFund} className="bg-petroleum text-white hover:bg-petroleum/90">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
